import { IPlaylist } from './types';

const STORAGE_KEY = 'cityportal-app-playlists-mock';

const now = () => new Date().toISOString();

const createId = () =>
  `playlist_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2)}`;

const normalizePlaylist = (playlist: IPlaylist): IPlaylist => ({
  ...playlist,
  allPlayers: !!playlist.allPlayers,
  players: playlist.players || [],
  groups: playlist.groups || [],
  items: (playlist.items || []).map((item, index) => ({
    ...item,
    position: index,
  })),
});

const readPlaylists = (): IPlaylist[] => {
  if (typeof localStorage === 'undefined') {
    return [];
  }

  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return [];
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return [];
  }
};

const writePlaylists = (playlists: IPlaylist[]) => {
  if (typeof localStorage === 'undefined') {
    return;
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(playlists));
};

export const playlistApi = {
  fetchAll: async (): Promise<IPlaylist[]> => readPlaylists(),

  fetchOne: async (id: string): Promise<IPlaylist> => {
    const playlist = readPlaylists().find(item => item._id === id);

    if (!playlist) {
      throw new Error('Playlist not found');
    }

    return playlist;
  },

  save: async (playlist: IPlaylist): Promise<IPlaylist> => {
    const playlists = readPlaylists();
    const existing = playlists.find(item => item._id === playlist._id);
    const timestamp = now();
    const normalized = normalizePlaylist({
      ...playlist,
      _id: playlist._id || createId(),
      createdAt:
        playlist.createdAt || (existing && existing.createdAt) || timestamp,
      updatedAt: timestamp,
    });

    const nextPlaylists = existing
      ? playlists.map(item => (item._id === normalized._id ? normalized : item))
      : [...playlists, normalized];

    writePlaylists(nextPlaylists);

    return normalized;
  },

  delete: async (id: string): Promise<void> => {
    writePlaylists(readPlaylists().filter(item => item._id !== id));
  },
};
